import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  ACTIVITY_CATEGORY_META,
  ActivityCategorySlug,
  AuthService,
  FavoritesService,
  ActivityApiService,
  buildActivityLocationSlugSegments,
  inferActivityCategorySlug,
  redirectToSignIn,
  SeoService,
} from '@cnt-workspace/data-access';
import { CntSymbolDirective, FooterComponent, NavbarComponent } from '@cnt-workspace/ui';
import { MiniMapComponent } from '@cnt-workspace/booking';
import { ActivityLocation, HelperFunctions } from '@curbnturf/entities';
import { EMPTY, Subscription, combineLatest } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';

@Component({
  selector: 'cnt-activity-details',
  standalone: true,
  imports: [CommonModule, RouterLink, NavbarComponent, FooterComponent, MiniMapComponent, CntSymbolDirective],
  templateUrl: './activity-details.component.html',
})
export class ActivityDetailsComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private seo = inject(SeoService);
  private activityApi = inject(ActivityApiService);
  private auth = inject(AuthService);
  private favorites = inject(FavoritesService);

  activity?: ActivityLocation;
  categorySlug?: ActivityCategorySlug;
  notFound = false;
  favorited = false;

  private lastRequestedId: number | null = null;
  private favoritesSub: Subscription | null = null;

  get categoryMeta() {
    const slug = this.categorySlug ?? (this.activity ? inferActivityCategorySlug(this.activity) : 'activity');
    return ACTIVITY_CATEGORY_META[slug];
  }

  get isOwner(): boolean {
    if (!this.activity || !this.auth.currentUser?.id) return false;
    return this.activity['userId'] === this.auth.currentUser.id || this.activity.user?.id === this.auth.currentUser.id;
  }

  get thingsToDo(): string {
    return this.activity?.getThingsToDo() ?? '';
  }

  get locationLabel(): string {
    return this.activity?.getLocation() ?? '';
  }

  get photoUrls(): string[] {
    return (this.activity?.photos ?? []).map((p) => HelperFunctions.getPhotoUrlOriginal(p));
  }

  ngOnInit(): void {
    this.favoritesSub = this.favorites.favorites$.subscribe(() => this.hydrateFavorite());
    void this.favorites.refreshNow();

    combineLatest([this.route.params, this.route.data]).subscribe(([params, data]) => {
      const id = parseInt(params['id'], 10);
      this.categorySlug = data['categorySlug'] as ActivityCategorySlug | undefined;
      if (!id) {
        this.notFound = true;
        return;
      }
      if (id !== this.lastRequestedId) {
        this.lastRequestedId = id;
        this.resolveActivity(id);
      }
    });
  }

  private resolveActivity(id: number): void {
    let resolved = false;
    this.activityApi
      .fetch(id)
      .pipe(timeout(15000), catchError(() => EMPTY))
      .subscribe({
        next: (data) => {
          resolved = true;
          this.activity = new ActivityLocation(data);
          this.applyActivity();
        },
        complete: () => {
          if (!resolved) this.notFound = true;
        },
      });
  }

  private applyActivity(): void {
    if (!this.activity) return;
    this.notFound = false;
    const activity = this.activity;
    const meta = this.categoryMeta;
    const slugPath = buildActivityLocationSlugSegments(meta.slug, activity).join('/');
    const heroImage = this.photoUrls[0];
    const name = activity.getName() || meta.label;
    const location = activity.getLocation();

    this.seo.update({
      title: `${name}${location ? ` — ${location}` : ''} | CurbNTurf`,
      description: (activity.description || this.thingsToDo || meta.label).slice(0, 155),
      url: slugPath,
      image: heroImage,
      imageAlt: name,
      type: 'website',
      robots: activity.hidden ? 'noindex, nofollow' : undefined,
    });

    this.seo.setStructuredData(this.buildJsonLd(slugPath, heroImage));
    this.hydrateFavorite();
  }

  private buildJsonLd(slugPath: string, heroImage?: string): object {
    const activity = this.activity!;
    const meta = this.categoryMeta;
    const name = activity.getName() || meta.label;
    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: this.seo.absUrl('/') },
        { '@type': 'ListItem', position: 2, name: meta.label, item: this.seo.absUrl(`/${meta.slug}`) },
        { '@type': 'ListItem', position: 3, name, item: this.seo.absUrl(slugPath) },
      ],
    };
    const main = {
      '@context': 'https://schema.org',
      '@type': meta.schemaType,
      name,
      description: (activity.description || this.thingsToDo || meta.label).replace(/\s+/g, ' ').trim().slice(0, 500),
      image: heroImage,
      url: this.seo.absUrl(slugPath),
      address: activity.address?.city
        ? { '@type': 'PostalAddress', addressLocality: activity.address.city, addressRegion: activity.address.state, addressCountry: 'US' }
        : undefined,
      geo: activity.coordinates
        ? { '@type': 'GeoCoordinates', latitude: activity.coordinates.lat, longitude: activity.coordinates.lon }
        : undefined,
    };
    return [main, breadcrumb] as unknown as object;
  }

  private hydrateFavorite(): void {
    if (!this.activity?.id) return;
    this.favorited = this.favorites.isFavorite('poi', this.activity.id);
  }

  toggleFavorite(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.activity?.id) return;
    if (!this.auth.currentUser) {
      redirectToSignIn(this.router, { favKind: 'poi', favId: String(this.activity.id) });
      return;
    }
    if (this.favorited) {
      void this.favorites.removeFavorite('poi', this.activity.id);
    } else {
      void this.favorites.addFavorite('poi', this.activity.id);
    }
    this.favorited = !this.favorited;
  }

  ngOnDestroy(): void {
    this.favoritesSub?.unsubscribe();
    this.seo.setStructuredData(null);
  }
}
